<script lang="ts">
  // Enter sends, Shift+Enter goes to the next line; one draft per conversation (and per
  // thread) in localStorage; @ suggests the conversation's members; a small formatting bar.
  // Files: the paper clip, a drop on the box, or a paste; each one is uploaded at once and
  // sent with the message.
  // Phase 2: in the main composer, `/tache …` creates a task (preview before sending); in a
  // task's detail (`handoffFor`), a second button sends and hands the turn over.
  import { tick, untrack } from 'svelte';
  import { api, ApiError, uploadFile, type Attachment, type Message, type UserRef } from '../lib/api';
  import { dueLabel, isTaskCommand, parseTaskCommand, suggestsCommand } from '../lib/command';
  import { handoffTarget } from '../lib/tasks';
  import { formatSize } from '../lib/format';
  import { app } from '../lib/stores.svelte';
  import { signalTyping } from '../lib/ws';
  import Avatar from './Avatar.svelte';

  let {
    conversationId,
    placeholder,
    rootId = null,
    handoffFor = null,
  }: { conversationId: string; placeholder: string; rootId?: string | null; handoffFor?: Message | null } = $props();

  const draftKey = (id: string, root: string | null) => (root ? `draft:${id}:${root}` : `draft:${id}`);
  function readDraft(key: string): string {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  }
  function writeDraft(key: string, text: string): void {
    try {
      if (text) localStorage.setItem(key, text);
      else localStorage.removeItem(key);
    } catch {
      // Private browsing or storage disabled: the draft is simply not kept.
    }
  }

  // Recreated for each conversation and thread ({#key}): the draft is read once.
  const key = untrack(() => draftKey(conversationId, rootId));
  let text = $state(readDraft(key));
  let sending = $state(false);
  let error = $state('');
  let textarea: HTMLTextAreaElement | undefined = $state();

  $effect(() => writeDraft(key, text));

  // Grow with the text, up to a limit.
  $effect(() => {
    void text;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`;
  });

  // --- Mentions -----------------------------------------------------------------------

  interface Suggestion {
    username: string;
    label: string;
    user?: UserRef;
    /** The `/tache` command rather than a mention. */
    command?: boolean;
  }
  let query = $state<string | null>(null);
  let queryStart = 0;
  let selected = $state(0);

  const suggestions = $derived.by((): Suggestion[] => {
    if (!rootId && suggestsCommand(text) && !text.includes(' ')) {
      return [{ username: 'tache', label: 'Créer une tâche', command: true }];
    }
    if (query === null) return [];
    const q = query.toLowerCase();
    const members = app.conversation(conversationId)?.members ?? [];
    const people: Suggestion[] = members
      .filter((m) => m.id !== app.me?.id)
      .filter((m) => m.username.toLowerCase().startsWith(q) || m.display_name.toLowerCase().includes(q))
      .map((m) => ({ username: m.username, label: m.display_name, user: m }));
    const everyone: Suggestion[] = 'canal'.startsWith(q) ? [{ username: 'canal', label: 'Tous les membres' }] : [];
    return [...people, ...everyone].slice(0, 8);
  });

  /** An @ being typed just before the cursor, at the start or after a space. */
  function detectMention() {
    if (!textarea) return;
    const before = text.slice(0, textarea.selectionStart);
    const match = /(^|\s)@([a-z0-9._-]*)$/i.exec(before);
    if (match) {
      query = match[2]!;
      queryStart = before.length - match[2]!.length - 1;
      selected = 0;
    } else {
      query = null;
    }
  }

  async function pick(s: Suggestion) {
    if (!textarea) return;
    if (s.command) {
      text = '/tache ';
      await tick();
      textarea.setSelectionRange(text.length, text.length);
      textarea.focus();
      return;
    }
    const end = textarea.selectionStart;
    text = `${text.slice(0, queryStart)}@${s.username} ${text.slice(end)}`;
    query = null;
    await tick();
    const caret = queryStart + s.username.length + 2;
    textarea.setSelectionRange(caret, caret);
    textarea.focus();
  }

  // --- Formatting ---------------------------------------------------------------------

  async function wrap(before: string, after = before, placeholderText = '') {
    if (!textarea) return;
    const { selectionStart: start, selectionEnd: end } = textarea;
    const inner = text.slice(start, end) || placeholderText;
    text = `${text.slice(0, start)}${before}${inner}${after}${text.slice(end)}`;
    await tick();
    textarea.focus();
    textarea.setSelectionRange(start + before.length, start + before.length + inner.length);
  }

  async function insertMention() {
    if (!textarea) return;
    const start = textarea.selectionStart;
    const space = start > 0 && !/\s/.test(text[start - 1] ?? '') ? ' ' : '';
    text = `${text.slice(0, start)}${space}@${text.slice(start)}`;
    await tick();
    textarea.focus();
    textarea.setSelectionRange(start + space.length + 1, start + space.length + 1);
    detectMention();
  }

  // --- Files --------------------------------------------------------------------------

  const MAX_FILES = 10;
  interface Upload {
    key: string;
    name: string;
    size: number;
    progress: number;
    attachment: Attachment | null;
    error: string;
    abort: () => void;
  }
  let uploads = $state<Upload[]>([]);
  let dragging = $state(false);
  let picker: HTMLInputElement | undefined = $state();
  const uploading = $derived(uploads.some((u) => !u.attachment && !u.error));
  const ready = $derived(uploads.filter((u) => u.attachment).map((u) => u.attachment!.id));

  function addFiles(files: Iterable<File>) {
    const maxBytes = (app.info?.max_upload_mb ?? 50) * 1024 * 1024;
    error = '';
    for (const file of files) {
      if (uploads.length >= MAX_FILES) {
        error = `${MAX_FILES} fichiers au plus par message`;
        break;
      }
      const upload: Upload = {
        key: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        progress: 0,
        attachment: null,
        error: '',
        abort: () => {},
      };
      if (file.size > maxBytes) upload.error = `Trop volumineux (${app.info?.max_upload_mb ?? 50} Mo au plus)`;
      else if (file.size === 0) upload.error = 'Fichier vide';
      uploads.push(upload);
      if (upload.error) continue;
      // Read back from the state: changes must go through the reactive proxy.
      const tracked = uploads.at(-1)!;
      const job = uploadFile(conversationId, file, (f) => (tracked.progress = f));
      tracked.abort = job.abort;
      job.done
        .then((attachment) => (tracked.attachment = attachment))
        .catch((e) => (tracked.error = e instanceof ApiError ? e.message : "Échec de l'envoi"));
    }
    textarea?.focus();
  }

  function removeUpload(key: string) {
    uploads.find((u) => u.key === key)?.abort();
    uploads = uploads.filter((u) => u.key !== key);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    dragging = false;
    if (event.dataTransfer?.files.length) addFiles(event.dataTransfer.files);
  }

  function onPaste(event: ClipboardEvent) {
    const files = event.clipboardData?.files;
    if (files && files.length > 0) {
      event.preventDefault();
      addFiles(files);
    }
  }

  // --- Tasks --------------------------------------------------------------------------

  const conversation = $derived(app.conversation(conversationId));
  /** `/tache …` typed in the main composer: what would be created, or why not. */
  const taskCommand = $derived.by(() => {
    if (rootId || !isTaskCommand(text)) return null;
    if (!conversation?.is_project) {
      const channel = conversation?.kind === 'public' || conversation?.kind === 'private';
      const how = !channel
        ? ''
        : conversation.role === 'admin' || app.me?.is_admin
          ? ' : activez l\'option projet avec le menu « ⋯ » en haut de la conversation'
          : ' : un administrateur du canal peut en faire un projet (menu « ⋯ » en haut de la conversation)';
      return { error: `Les tâches sont réservées aux discussions projet${how}` } as const;
    }
    const parsed = parseTaskCommand(text);
    if (!parsed.ok) return { error: parsed.error } as const;
    const { assignee: username, ...rest } = parsed.task;
    const assignee = username ? conversation.members.find((m) => m.username === username) : null;
    if (username && !assignee) return { error: `@${username} n'est pas membre du projet` } as const;
    return { ...rest, assignee: assignee ?? null } as const;
  });

  async function createTask() {
    if (!taskCommand || 'error' in taskCommand || sending || uploading) return;
    sending = true;
    error = '';
    try {
      const message = await api.createTask(conversationId, {
        title: taskCommand.title,
        assignee_id: taskCommand.assignee?.id ?? null,
        due_date: taskCommand.due,
        body: taskCommand.body,
        attachment_ids: ready,
        client_id: crypto.randomUUID(),
      });
      app.addMessage(message, false);
      app.upsertTask(message);
      text = '';
      uploads = [];
    } catch (e) {
      error = e instanceof ApiError ? e.message : "La tâche n'a pas pu être créée";
    } finally {
      sending = false;
      textarea?.focus();
    }
  }

  /** In a task's detail: to whom « Envoyer et passer la main » hands the turn. */
  const handoffCandidates = $derived(
    handoffFor?.task
      ? (conversation?.members ?? []).filter((m) => m.id !== handoffFor!.task!.waiting_on?.id && m.id !== app.me?.id)
      : [],
  );
  let handoffTo = $state<string | null>(null);
  const proposed = $derived(handoffFor?.task && app.me ? handoffTarget(handoffFor.task, app.me.id) : null);
  const target = $derived(
    handoffCandidates.find((m) => m.id === handoffTo) ?? handoffCandidates.find((m) => m.id === proposed?.id) ?? handoffCandidates[0] ?? null,
  );
  let choosing = $state(false);

  async function sendHandoff() {
    if (!handoffFor || !target || sending || uploading) return;
    if (!text.trim() && ready.length === 0) {
      error = 'Ajoutez un commentaire ou un document pour passer la main';
      return;
    }
    sending = true;
    error = '';
    try {
      const task = await api.handoff(handoffFor.id, target.id, text, ready, crypto.randomUUID());
      app.upsertTask(task);
      void app.loadThread(handoffFor.id);
      text = '';
      uploads = [];
      handoffTo = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "La main n'a pas pu être passée";
    } finally {
      sending = false;
      textarea?.focus();
    }
  }

  // --- Sending ------------------------------------------------------------------------

  async function send() {
    if (taskCommand) return createTask();
    const body = text;
    if ((!body.trim() && ready.length === 0) || sending || uploading) return;
    sending = true;
    error = '';
    // The same client_id on a retry never creates a second message.
    const clientId = crypto.randomUUID();
    try {
      const message = await api.send(conversationId, body, clientId, rootId, ready);
      app.addMessage(message, false);
      text = '';
      uploads = [];
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Le message n'a pas pu être envoyé";
    } finally {
      sending = false;
      textarea?.focus();
    }
  }

  function onKeydown(event: KeyboardEvent) {
    if (suggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        selected = (selected + step + suggestions.length) % suggestions.length;
        return;
      }
      if ((event.key === 'Enter' || event.key === 'Tab') && !event.shiftKey) {
        event.preventDefault();
        void pick(suggestions[selected]!);
        return;
      }
      if (event.key === 'Escape') {
        query = null;
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void send();
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
      event.preventDefault();
      void wrap('**', '**', 'texte');
    }
  }
</script>

<form
  class="composer"
  class:thread={!!rootId}
  onsubmit={(e) => {
    e.preventDefault();
    void send();
  }}
  ondragover={(e) => {
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault();
      dragging = true;
    }
  }}
  ondragleave={(e) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) dragging = false;
  }}
  ondrop={onDrop}
>
  {#if error}<p class="error" role="alert">{error}</p>{/if}
  {#if taskCommand}
    {#if 'error' in taskCommand}
      <p class="task-preview invalid" role="alert">{taskCommand.error}</p>
    {:else}
      <p class="task-preview" role="status">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="17" rx="3" /><path d="M8 11l2.5 2.5L16 8" /></svg>
        <span>
          {#if taskCommand.assignee}Nouvelle tâche pour <strong>{taskCommand.assignee.display_name}</strong>{:else}Nouvelle tâche, <strong>sans responsable</strong>{/if}
          {#if taskCommand.due}· échéance <strong>{dueLabel(taskCommand.due)}</strong>{/if}
          · demandée par vous{taskCommand.body ? ' · avec une description' : ''}
        </span>
      </p>
    {/if}
  {/if}
  {#if suggestions.length > 0}
    <ul class="suggestions" role="listbox" aria-label="Mentionner">
      {#each suggestions as s, i (s.username)}
        <li role="option" aria-selected={i === selected}>
          <button type="button" class:selected={i === selected} onmousedown={(e) => e.preventDefault()} onclick={() => pick(s)}>
            {#if s.user}<Avatar user={s.user} size={22} />{:else}<span class="all" aria-hidden="true">{s.command ? '/' : '@'}</span>{/if}
            <span>{s.label}</span><span class="muted">{s.command ? '/tache @personne échéance intitulé' : `@${s.username}`}</span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
  <div class="box" class:dragging>
    {#if dragging}<p class="drop">Déposez les fichiers pour les joindre</p>{/if}
    {#if uploads.length > 0}
      <ul class="uploads" aria-label="Fichiers joints">
        {#each uploads as u (u.key)}
          <li class="upload" class:failed={!!u.error}>
            <span class="upload-text">
              <span class="upload-name">{u.name}</span>
              <span class="upload-meta">
                {#if u.error}{u.error}{:else if u.attachment}{formatSize(u.size)}{:else}Envoi… {Math.round(u.progress * 100)} %{/if}
              </span>
            </span>
            {#if !u.attachment && !u.error}
              <progress max="1" value={u.progress} aria-label="Envoi de {u.name}"></progress>
            {/if}
            <button type="button" class="upload-remove" aria-label="Retirer {u.name}" title="Retirer" onclick={() => removeUpload(u.key)}>
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
    <div class="tools" role="toolbar" aria-label="Mise en forme">
      <button type="button" title="Joindre des fichiers" aria-label="Joindre des fichiers" onclick={() => picker?.click()}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5l-8.6 8.6a5.5 5.5 0 01-7.8-7.8l8.6-8.6a3.7 3.7 0 015.2 5.2l-8.6 8.6a1.8 1.8 0 01-2.6-2.6l7.9-7.9" /></svg>
      </button>
      <input
        bind:this={picker}
        type="file"
        multiple
        hidden
        onchange={(e) => {
          const input = e.currentTarget as HTMLInputElement;
          if (input.files) addFiles(input.files);
          input.value = '';
        }}
      />
      <button type="button" title="Gras (Ctrl+B)" aria-label="Gras" onclick={() => wrap('**', '**', 'texte')}><strong>B</strong></button>
      <button type="button" title="Code" aria-label="Code" onclick={() => wrap('`', '`', 'code')}><code>&lt;/&gt;</code></button>
      <button type="button" title="Lien" aria-label="Lien" onclick={() => wrap('[', '](https://)', 'texte')}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1" /></svg>
      </button>
      <button type="button" title="Mentionner" aria-label="Mentionner" onclick={insertMention}>@</button>
    </div>
    <div class="line">
      <label for="composer-{conversationId}-{rootId ?? 'main'}" class="sr-only">{placeholder}</label>
      <textarea
        id="composer-{conversationId}-{rootId ?? 'main'}"
        bind:this={textarea}
        bind:value={text}
        rows="1"
        {placeholder}
        onkeydown={onKeydown}
        oninput={() => {
          signalTyping(conversationId);
          detectMention();
        }}
        onclick={detectMention}
        onpaste={onPaste}
        onblur={() => (query = null)}
      ></textarea>
      {#if taskCommand}
        <button type="submit" class="send" disabled={sending || uploading || 'error' in taskCommand}>Créer la tâche</button>
      {:else if handoffFor}
        <button type="submit" class="send secondary" disabled={sending || uploading || (!text.trim() && ready.length === 0)}>Commenter</button>
      {:else}
        <button type="submit" class="send" disabled={sending || uploading || (!text.trim() && ready.length === 0)}>{rootId ? 'Répondre' : 'Envoyer'}</button>
      {/if}
    </div>
  </div>
  {#if handoffFor && target}
    <div class="handoff">
      <button type="button" class="send handoff-send" disabled={sending || uploading} onclick={sendHandoff}>
        Envoyer et passer la main à {target.display_name}
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      </button>
      <button type="button" class="choose" aria-expanded={choosing} aria-label="Choisir à qui passer la main" onclick={() => (choosing = !choosing)}>
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {#if choosing}
        <ul class="targets" role="listbox" aria-label="Passer la main à">
          {#each handoffCandidates as m (m.id)}
            <li role="option" aria-selected={m.id === target.id}>
              <button type="button" onclick={() => { handoffTo = m.id; choosing = false; }}>
                <Avatar user={m} size={22} /><span>{m.display_name}</span>
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
  <p class="hint">
    {#if taskCommand}Maj+Entrée pour ajouter une description · joignez un .md pour la spec{:else if handoffFor}{handoffFor.task?.waiting_on && handoffFor.task.waiting_on.id !== app.me?.id ? `La main est à ${handoffFor.task.waiting_on.display_name} : votre commentaire ne la lui reprend pas.` : 'Un commentaire ne passe pas la main ; le bouton du dessous, si.'}{:else}Entrée pour envoyer, Maj+Entrée pour aller à la ligne, @ pour mentionner, /tache pour une tâche{/if}
  </p>
</form>

<style>
  .composer {
    position: relative;
    padding: 8px 24px 16px;
    max-width: calc(var(--reading-max) + 84px);
  }
  .composer.thread {
    padding: 8px 16px 16px;
  }
  .box {
    position: relative;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-l);
    background: var(--bg);
  }
  .box:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }
  .box.dragging {
    border-color: var(--accent);
    border-style: dashed;
    background: var(--accent-soft);
  }
  .drop {
    margin: 0;
    padding: 10px 12px 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--accent-on-soft);
  }
  .uploads {
    list-style: none;
    margin: 0;
    padding: 8px 8px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .upload {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 260px;
    padding: 6px 6px 6px 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    background: var(--bg-page);
  }
  .upload.failed {
    border-color: var(--danger);
    background: var(--danger-soft);
  }
  .upload-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .upload-name {
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .upload-meta {
    font-size: 12px;
    color: var(--muted);
  }
  .upload.failed .upload-meta {
    color: var(--danger);
  }
  .upload progress {
    width: 48px;
    height: 4px;
    accent-color: var(--accent);
  }
  .upload-remove {
    flex: 0 0 24px;
    height: 24px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .upload-remove:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .tools {
    display: flex;
    gap: 2px;
    padding: 4px 6px 0;
  }
  .tools button {
    width: 30px;
    height: 28px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
  }
  .tools button:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .tools code {
    font-family: var(--font-mono);
    font-size: 11px;
  }
  .line {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 2px 8px 8px 12px;
  }
  textarea {
    flex: 1;
    min-height: 24px;
    max-height: 240px;
    border: 0;
    outline: none;
    resize: none;
    background: transparent;
    line-height: 20px;
    padding: 6px 0;
  }
  .send {
    height: 36px;
    padding: 0 14px;
    border: 0;
    border-radius: var(--radius-m);
    background: var(--accent);
    color: #ffffff;
    font-weight: 600;
  }
  .send:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .task-preview {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    padding: 6px 10px;
    border-radius: 8px;
    background: var(--accent-soft);
    color: var(--accent-on-soft);
    font-size: 13px;
  }
  .task-preview.invalid {
    background: var(--danger-soft);
    color: var(--danger-text);
  }
  .send.secondary {
    background: var(--bg);
    color: var(--ink);
    border: 1px solid var(--border-strong);
  }
  .handoff {
    position: relative;
    display: flex;
    margin-top: 8px;
  }
  .handoff-send {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: var(--radius-m) 0 0 var(--radius-m);
  }
  .choose {
    width: 40px;
    border: 0;
    border-left: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 0 var(--radius-m) var(--radius-m) 0;
    background: var(--accent);
    color: #ffffff;
  }
  .targets {
    position: absolute;
    bottom: calc(100% + 4px);
    right: 0;
    z-index: 10;
    width: min(280px, 100%);
    margin: 0;
    padding: 4px;
    list-style: none;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.16);
  }
  .targets button {
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    text-align: left;
  }
  .targets button:hover,
  .targets [aria-selected='true'] button {
    background: var(--accent-selected);
  }
  .hint {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--muted);
  }
  .error {
    margin-bottom: 8px;
  }
  .suggestions {
    position: absolute;
    bottom: calc(100% - 4px);
    left: 24px;
    z-index: 10;
    width: min(320px, calc(100% - 48px));
    margin: 0;
    padding: 4px;
    list-style: none;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    box-shadow: 0 8px 24px rgba(16, 24, 40, 0.16);
  }
  .suggestions button {
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 8px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    text-align: left;
  }
  .suggestions button.selected {
    background: var(--accent-selected);
  }
  .muted {
    color: var(--muted);
    font-size: 12px;
  }
  .all {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
  }
  @media (max-width: 759px) {
    .composer {
      padding: 8px 12px 12px;
    }
    .hint {
      display: none;
    }
    .send {
      height: 44px;
    }
  }
</style>
